import React, { useState } from "react";
import { CheckCircle2Icon, Loader2Icon } from "lucide-react";
export type FormField = {
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "select" | "textarea";
  options?: string[];
  required?: boolean;
  half?: boolean;
};
type InquiryFormProps = {
  fields: FormField[];
  submitLabel: string;
  successTitle: string;
  successText: string;
};
type Status = "idle" | "sending" | "sent" | "error";
export function InquiryForm({
  fields,
  submitLabel,
  successTitle,
  successText
}: InquiryFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const missing = fields.some(f => f.required && !String(data.get(f.name) ?? "").trim());
    if (missing) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    window.setTimeout(() => setStatus("sent"), 900);
  };
  if (status === "sent") {
    return <div role="status" className="flex flex-col items-start gap-3 rounded-2xl bg-ink p-8 text-paper" data-id="element-66" data-mp="e8ukp5363bc1">
        <CheckCircle2Icon className="h-7 w-7 text-amber" data-id="element-67" data-mp="9a15n888fc9r" />
        <h3 className="text-xl font-semibold" data-id="element-68" data-mp="nxchlb8bsyae">{successTitle}</h3>
        <p className="text-paper/70" data-id="element-69" data-mp="nyxev8m0yvby">{successText}</p>
        <button type="button" onClick={() => setStatus("idle")} className="mt-2 text-sm font-medium underline underline-offset-4" data-id="element-70" data-mp="j6mb1uo5ype4">
          Send another
        </button>
      </div>;
  }
  const inputCls = "w-full rounded-lg border border-line bg-white px-3 py-2.5 text-ink outline-none transition-colors duration-150 focus:border-ink focus:ring-2 focus:ring-ink/10";
  return <form onSubmit={onSubmit} noValidate className="grid grid-cols-2 gap-4" data-id="element-71" data-mp="11wvpq41509mp">
      {fields.map(f => <label key={f.name} className={`flex flex-col gap-1.5 text-sm font-medium text-ink ${f.half ? "col-span-2 sm:col-span-1" : "col-span-2"}`} data-id="element-72" data-mp="38n1uvjtdsc0z">
          <span data-id="element-73" data-mp="33quz2jup8gwr">
            {f.label}
            {f.required && <span className="text-muted" data-id="element-74" data-mp="17mdt3fcp234a"> *</span>}
          </span>
          {f.type === "select" ? <select name={f.name} className={inputCls} defaultValue="" data-id="element-75" data-mp="1clqdltg3ey3p">
              <option value="" disabled data-id="element-76" data-mp="1gmjzyidw04uh">Select…</option>
              {f.options?.map(o => <option key={o} data-id="element-77" data-mp="1gon03v8r7ecx">{o}</option>)}
            </select> : f.type === "textarea" ? <textarea name={f.name} rows={4} className={inputCls} data-id="element-78" data-mp="1qkho75vqy1n3" /> : <input name={f.name} type={f.type ?? "text"} className={inputCls} data-id="element-79" data-mp="1qidlgr9l38oe" />}
        </label>)}
      {status === "error" && <p role="alert" className="col-span-2 text-sm text-red-700" data-id="element-80" data-mp="20a190h30s5kl">Please fill in the required fields.</p>}
      <button type="submit" disabled={status === "sending"} className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 font-medium text-paper transition-colors duration-150 hover:bg-ink/85 disabled:opacity-70" data-id="element-81" data-mp="24atk7fvgckji">
        {status === "sending" && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-82" data-mp="2oesds7y90r6x" />}
        {status === "sending" ? "Sending…" : submitLabel}
      </button>
    </form>;
}